import { Box, Card, CardContent, Typography, Chip, LinearProgress, useTheme, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { PackingOperation } from '../schema';
import LinkedToTripButton from './LinkedToTripButton';

interface Props {
    operation: PackingOperation;
    completedCount: number;
    totalCount: number;
}

export default function OperationHeader({ operation, completedCount, totalCount }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const trip = (operation as { trip?: { id?: string; name?: string } | null }).trip ?? null;
    const sourceEvents = operation.sourceEvents || [];
    const targetEvents = operation.targetEvents || [];
    const sourceStorageLocations = operation.sourceStorageLocations || [];
    const targetStorageLocations = operation.targetStorageLocations || [];

    type SourceOrTarget = { id?: string; name: string };
    const sources: SourceOrTarget[] = [
        ...sourceStorageLocations.map((s: { name?: string }) => ({ name: s.name ?? '' })).filter((x: SourceOrTarget) => Boolean(x.name)),
        ...sourceEvents
            .map((e: { id?: string; name?: string }) => ({ id: e.id, name: e.name ?? '' }))
            .filter((x: SourceOrTarget) => Boolean(x.name)),
    ];
    const targets: SourceOrTarget[] = [
        ...targetEvents
            .map((e: { id?: string; name?: string }) => ({ id: e.id, name: e.name ?? '' }))
            .filter((x: SourceOrTarget) => Boolean(x.name)),
        ...targetStorageLocations.map((s: { name?: string }) => ({ name: s.name ?? '' })).filter((x: SourceOrTarget) => Boolean(x.name)),
    ];

    // Calculate progress
    const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
    const isComplete = progressPercentage === 100;

    return (
        <Card sx={{ mb: 3, boxShadow: 2, overflow: 'hidden' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 }, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'flex-start',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 2,
                        flexDirection: isMobile ? 'column' : 'row',
                    }}
                >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}>
                            Packing Operation
                        </Typography>
                        {trip?.id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                <LinkedToTripButton trip={{ id: trip.id, name: trip.name }} />
                            </Box>
                        )}
                        {trip && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                Quantities from trip plan
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Created: {new Date(operation.createdAt).toLocaleDateString()}
                        </Typography>
                        {operation.completedAt && (
                            <Typography variant="body2" color="text.secondary">
                                Completed: {new Date(operation.completedAt).toLocaleDateString()}
                            </Typography>
                        )}
                    </Box>
                    <Chip
                        label={operation.status === 'active' ? 'Active' : 'Completed'}
                        color={operation.status === 'active' ? 'success' : 'default'}
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                {/* Sources and Targets — stacked on mobile for clarity */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: isMobile ? 'stretch' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 1.5 : 1,
                        mb: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
                        {isMobile && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={{ width: '100%', display: 'block', mb: 0.5 }}
                            >
                                From
                            </Typography>
                        )}
                        {sources.map((source, idx) =>
                            source.id ? (
                                <Chip
                                    key={`${source.id}-${idx}`}
                                    label={source.name}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(`/event/${source.id}/inventory`)}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <Chip key={`s-${idx}`} label={source.name} size="small" variant="outlined" />
                            )
                        )}
                    </Box>
                    <ArrowForwardIcon
                        sx={{
                            color: 'text.secondary',
                            alignSelf: isMobile ? 'center' : 'center',
                            flexShrink: 0,
                            ...(isMobile && { transform: 'rotate(90deg)' }),
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
                        {isMobile && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={{ width: '100%', display: 'block', mb: 0.5 }}
                            >
                                To
                            </Typography>
                        )}
                        {targets.length > 0 ? (
                            targets.map((target, idx) =>
                                target.id ? (
                                    <Chip
                                        key={`${target.id}-${idx}`}
                                        label={target.name}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => navigate(`/event/${target.id}/inventory`)}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ) : (
                                    <Chip key={`t-${idx}`} label={target.name} size="small" variant="outlined" color="primary" />
                                )
                            )
                        ) : (
                            <Chip label="HQ" size="small" variant="outlined" />
                        )}
                    </Box>
                </Box>

                {/* Overall Progress */}
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {isMobile ? (
                                <>
                                    {completedCount}/{totalCount} tasks ({Math.round(progressPercentage)}%)
                                </>
                            ) : (
                                <>
                                    {completedCount} / {totalCount} tasks completed ({Math.round(progressPercentage)}%)
                                </>
                            )}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: isComplete ? 'success.main' : 'primary.main',
                            },
                        }}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}
